import { Directive, Input, OnChanges, forwardRef } from '@angular/core';
import { CdkVirtualScrollViewport, VIRTUAL_SCROLL_STRATEGY, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/** How far below the viewport's top edge a page's top may sit and still count as "current". */
const VISIBLE_TOLERANCE_PX = 50;
/** Viewports of headroom kept rendered above / below the visible area (was rootMargin 150%). */
const BUFFER_VIEWPORTS = 1.5;

/**
 * Virtual-scroll strategy for a document whose page heights are all known up front.
 *
 * The offset table built here — not DOM flow — is the single source of truth for
 * scrollbar geometry. Nothing the browser does while rendering a page can move
 * another page, so drag / goto land exactly and stay put.
 */
export class PdfPagesVirtualScrollStrategy implements VirtualScrollStrategy {
  private readonly indexChange$ = new Subject<number>();
  readonly scrolledIndexChange: Observable<number> = this.indexChange$.pipe(distinctUntilChanged());

  private viewport: CdkVirtualScrollViewport | null = null;
  private heights: number[] = [];
  /** Top edge of page i's box (excluding the gap above it). */
  private offsets: number[] = [];
  private gap = 16;
  private total = 0;
  /** Viewports of headroom kept rendered above/below (configurable per consumer). */
  private bufferViewports = BUFFER_VIEWPORTS;

  setBufferViewports(n: number): void {
    const clamped = Number.isFinite(n) && n >= 0 ? n : BUFFER_VIEWPORTS;
    if (clamped === this.bufferViewports) { return; }
    this.bufferViewports = clamped;
    this.updateRenderedRange();
  }

  setPageHeights(heightsPx: number[], gapPx: number): void {
    const vp = this.viewport;

    // Remember which page sits under the top edge (and how far into it we are)
    // so a relayout doesn't visibly move the user's position.
    let anchorIndex = -1;
    let anchorDelta = 0;
    if (vp && this.offsets.length) {
      const scroll = vp.measureScrollOffset();
      anchorIndex = this.findFirstVisible(scroll, vp.getViewportSize());
      anchorDelta = scroll - (this.offsets[anchorIndex] - this.gap);
    }

    this.gap = gapPx;
    this.heights = heightsPx.slice();
    this.offsets = new Array(this.heights.length);
    let y = gapPx;
    for (let i = 0; i < this.heights.length; i++) {
      this.offsets[i] = y;
      y += this.heights[i] + gapPx;
    }
    this.total = y;

    if (!vp) { return; }
    vp.setTotalContentSize(this.total);
    if (anchorIndex >= 0 && anchorIndex < this.offsets.length) {
      vp.scrollToOffset(this.offsets[anchorIndex] - this.gap + anchorDelta, 'auto');
    }
    this.updateRenderedRange();
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    viewport.setTotalContentSize(this.total);
    this.updateRenderedRange();
  }

  detach(): void {
    this.indexChange$.complete();
    this.viewport = null;
  }

  onContentScrolled(): void { this.updateRenderedRange(); }
  onDataLengthChanged(): void { this.updateRenderedRange(); }
  onContentRendered(): void { /* offsets are authoritative; nothing to measure */ }
  onRenderedOffsetChanged(): void { /* no-op */ }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this.viewport || !this.offsets.length) { return; }
    const i = Math.min(Math.max(0, index), this.offsets.length - 1);
    // Land with the gap above the page visible — same lead-in the old scroller used.
    this.viewport.scrollToOffset(this.offsets[i] - this.gap, behavior);
  }

  private findFirstVisible(scrollOffset: number, viewportSize: number): number {
    const n = this.offsets.length;
    if (n === 0) { return 0; }
    // Edge clamps: at the very top / very bottom report the first / last page even
    // when that page is shorter than the viewport and its top isn't at the edge.
    if (scrollOffset <= 1) { return 0; }
    if (scrollOffset + viewportSize >= this.total - 1) { return n - 1; }

    const cutoff = scrollOffset + VISIBLE_TOLERANCE_PX;
    let lo = 0;
    let hi = n - 1;
    let ans = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (this.offsets[mid] - this.gap <= cutoff) { ans = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    return ans;
  }

  /** Smallest i whose bottom edge is below y; n-1 if none. */
  private firstIndexWithBottomAfter(y: number): number {
    const n = this.offsets.length;
    let lo = 0;
    let hi = n - 1;
    let ans = n - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (this.offsets[mid] + this.heights[mid] > y) { ans = mid; hi = mid - 1; }
      else { lo = mid + 1; }
    }
    return ans;
  }

  /** Smallest i whose (gap-inclusive) top edge is below y; n if none. */
  private firstIndexWithTopAfter(y: number): number {
    const n = this.offsets.length;
    let lo = 0;
    let hi = n - 1;
    let ans = n;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (this.offsets[mid] - this.gap > y) { ans = mid; hi = mid - 1; }
      else { lo = mid + 1; }
    }
    return ans;
  }

  private updateRenderedRange(): void {
    const vp = this.viewport;
    if (!vp) { return; }
    const n = this.offsets.length;
    if (n === 0) {
      vp.setRenderedRange({ start: 0, end: 0 });
      vp.setRenderedContentOffset(0);
      return;
    }

    const scroll = vp.measureScrollOffset();
    const size = vp.getViewportSize();
    const first = this.findFirstVisible(scroll, size);

    let start = this.firstIndexWithBottomAfter(scroll - this.bufferViewports * size);
    let end = this.firstIndexWithTopAfter(scroll + size + this.bufferViewports * size);
    start = Math.max(0, Math.min(start, n - 1));
    end = Math.max(start + 1, Math.min(end, n));

    vp.setRenderedRange({ start, end });
    vp.setRenderedContentOffset(this.offsets[start] - this.gap);
    this.indexChange$.next(first);
  }
}

export function pdfPagesScrollStrategyFactory(directive: PdfPagesScrollDirective): PdfPagesVirtualScrollStrategy {
  return directive.strategy;
}

/**
 * Attaches PdfPagesVirtualScrollStrategy to a <cdk-virtual-scroll-viewport>.
 * Same provider pattern CDK uses for its own `cdkFixedSizeVirtualScroll`.
 */
@Directive({
  selector: 'cdk-virtual-scroll-viewport[pdfPagesScroll]',
  standalone: true,
  providers: [{
    provide: VIRTUAL_SCROLL_STRATEGY,
    useFactory: pdfPagesScrollStrategyFactory,
    deps: [forwardRef(() => PdfPagesScrollDirective)],
  }],
})
export class PdfPagesScrollDirective implements OnChanges {
  @Input() pageHeights: number[] = [];
  @Input() gapPx = 16;
  /** Viewports of rendered headroom above/below the visible area (default 1.5). */
  @Input() bufferViewports = BUFFER_VIEWPORTS;

  readonly strategy = new PdfPagesVirtualScrollStrategy();

  ngOnChanges(): void {
    this.strategy.setBufferViewports(this.bufferViewports);
    this.strategy.setPageHeights(this.pageHeights ?? [], this.gapPx);
  }
}
